import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { rpc } from '../../api/rpc'
import type { ChatMessageMatch, ChatSearchResults, ChatSummary } from '../../api/types'
import { cn } from '../../lib/cn'
import { useStatusBarDim } from '../../lib/theme'
import { IconChat, IconClose, IconSearch } from '../icons'

interface ChatSearchModalProps {
  open: boolean
  onClose(): void
}

type FlattenedItem =
  | { type: 'chat'; data: ChatSummary }
  | { type: 'message'; data: ChatMessageMatch }

export function ChatSearchModal({ open, onClose }: ChatSearchModalProps) {
  const { t } = useTranslation()
  useStatusBarDim(open)
  const navigate = useNavigate()
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<ChatSearchResults>({ chats: [], messages: [] })
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Reset state when opening/closing
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults({ chats: [], messages: [] })
      setSelectedIndex(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Search debounce
  useEffect(() => {
    if (!open) return
    const trimmed = query.trim()
    if (!trimmed) {
      setResults({ chats: [], messages: [] })
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await rpc.call<ChatSearchResults>('chats.search', { query: trimmed, limit: 30 })
        if (active) {
          setResults(res)
          setSelectedIndex(0)
        }
      } catch (err) {
        if (active) {
          setResults({ chats: [], messages: [] })
        }
      } finally {
        if (active) setLoading(false)
      }
    }, 200)

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [open, query])

  const flattened: FlattenedItem[] = [
    ...results.chats.map(data => ({ type: 'chat' as const, data })),
    ...results.messages.map(data => ({ type: 'message' as const, data }))
  ]

  const handleSelect = (item: FlattenedItem) => {
    onClose()
    if (item.type === 'chat') {
      navigate(`/chat/${item.data.id}`)
    } else {
      navigate(`/chat/${item.data.chatId}`)
    }
  }

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }

      if (flattened.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => (prev + 1) % flattened.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => (prev - 1 + flattened.length) % flattened.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (flattened[selectedIndex]) {
          handleSelect(flattened[selectedIndex])
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, flattened, selectedIndex])

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return
    const activeEl = listRef.current.querySelector<HTMLElement>('[data-active="true"]')
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (!open) return null

  let currentIndex = 0

  return createPortal(
    <div className="fixed inset-x-0 top-0 z-50 flex h-[var(--app-h)] items-start justify-center p-4 pt-[calc(4rem+env(safe-area-inset-top,0px))] sm:pt-24">
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('common.close')}
        className="absolute inset-0 h-full w-full bg-[rgb(20_20_18_/_0.4)] dark:bg-[rgb(0_0_0_/_0.55)] transition-opacity cursor-pointer border-0 p-0"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
      />
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative z-10 flex max-h-[80vh] w-full max-w-[620px] flex-col overflow-hidden rounded-2xl border border-line bg-cream-50 shadow-sheet outline-none'
        )}
      >
        <div className="sr-only" id={titleId}>{t('chat.quickSearch')}</div>

        {/* Search header / input */}
        <div className="flex items-center gap-3 border-b border-line bg-cream-50 px-4 py-3">
          <IconSearch width={20} height={20} className="shrink-0 text-ink-400" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('chat.searchPlaceholder')}
            className="flex-1 bg-transparent text-base text-ink-100 outline-none placeholder:text-ink-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="rounded-md p-1 text-ink-400 hover:bg-cream-100 hover:text-ink-100"
              aria-label={t('common.close')}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-cream-300 bg-cream-100 px-1.5 py-0.5 text-[11px] font-medium text-ink-500">
            ESC
          </kbd>
        </div>

        {/* Results container */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-2 min-h-[140px] max-h-[460px]">
          {loading && !results.chats.length && !results.messages.length ? (
            <div className="flex h-32 items-center justify-center text-sm text-ink-400">
              {t('common.loading')}
            </div>
          ) : query.trim() && !results.chats.length && !results.messages.length ? (
            <div className="flex h-32 items-center justify-center text-sm text-ink-400">
              {t('chat.noSearchResults')}
            </div>
          ) : !query.trim() ? (
            <div className="flex h-32 items-center justify-center text-sm text-ink-400">
              {t('chat.searchPlaceholder')}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Chat titles section */}
              {results.chats.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    {t('chat.searchChatsGroup')}
                  </div>
                  <div className="mt-1 space-y-1">
                    {results.chats.map(chat => {
                      const itemIdx = currentIndex++
                      const isActive = itemIdx === selectedIndex
                      return (
                        <div
                          key={`chat-${chat.id}`}
                          data-active={isActive}
                          onClick={() => handleSelect({ type: 'chat', data: chat })}
                          onMouseEnter={() => setSelectedIndex(itemIdx)}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-[7px] px-3 py-2 text-sm transition-colors',
                            isActive ? 'bg-ginger-50 text-ginger-700' : 'text-ink-100 hover:bg-cream-200'
                          )}
                        >
                          <IconChat width={18} height={18} className={cn('shrink-0', isActive ? 'text-ginger-500' : 'text-ink-400')} />
                          <div className="flex-1 truncate font-medium">
                            {chat.title || t('chat.untitled')}
                          </div>
                          <span className="text-xs text-ink-400 shrink-0">
                            {new Date(chat.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Chat messages section */}
              {results.messages.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    {t('chat.searchMessagesGroup')}
                  </div>
                  <div className="mt-1 space-y-1">
                    {results.messages.map(msg => {
                      const itemIdx = currentIndex++
                      const isActive = itemIdx === selectedIndex
                      return (
                        <div
                          key={`msg-${msg.chatId}-${msg.messageId}`}
                          data-active={isActive}
                          onClick={() => handleSelect({ type: 'message', data: msg })}
                          onMouseEnter={() => setSelectedIndex(itemIdx)}
                          className={cn(
                            'flex cursor-pointer flex-col gap-1 rounded-[7px] px-3 py-2 text-sm transition-colors',
                            isActive ? 'bg-ginger-50 text-ginger-700' : 'text-ink-100 hover:bg-cream-200'
                          )}
                        >
                          <div className="flex items-center justify-between text-xs">
                            <span className={cn('font-medium truncate max-w-[70%]', isActive ? 'text-ginger-600' : 'text-ink-500')}>
                              {msg.chatTitle || t('chat.untitled')}
                            </span>
                            <span className="text-ink-400">
                              {msg.role === 'user' ? t('common.user', 'User') : 'Meow'}
                            </span>
                          </div>
                          <div className="line-clamp-2 text-xs text-ink-600 rounded-[7px] bg-cream-100 p-1.5 font-mono">
                            {msg.snippet}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer shortcuts helper */}
        <div className="flex items-center justify-between border-t border-line bg-cream-50 px-4 py-2 text-[11px] text-ink-500">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded border border-cream-300 bg-cream-50 px-1 py-0.5">↑</kbd>{' '}
              <kbd className="rounded border border-cream-300 bg-cream-50 px-1 py-0.5">↓</kbd>{' '}
              {t('common.navigate', 'Navigate')}
            </span>
            <span>
              <kbd className="rounded border border-cream-300 bg-cream-50 px-1.5 py-0.5">↵</kbd>{' '}
              {t('common.select', 'Select')}
            </span>
          </div>
          <span>
            <kbd className="rounded border border-cream-300 bg-cream-50 px-1 py-0.5">ESC</kbd>{' '}
            {t('common.close', 'Close')}
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}
